import axios, { CancelToken, CancelTokenSource } from 'axios';
import { useCallback, useEffect, useState, useRef } from 'react';

type CacheEntry<T> = {
  data: T;
  timestamp: number;
};

export type QueryKey =
  | string
  | null
  | (string | number | boolean | null | undefined)[]
  | Record<string, any>;

type QueryFunction<T> = (
  queryKey: QueryKey,
  cancelToken?: CancelToken
) => Promise<T>;

type UseQueryOptions = {
  cacheTTL?: number; // Time to live in milliseconds
  cacheKeyPrefix?: string; // Prefix for cache keys to avoid collisions
  /** When false, the query does not run automatically; use `refetch()` to fetch. Defaults to true. */
  autoLoadingEnabled?: boolean;
};

type UseQueryReturn<T> = {
  data: T;
  errors: string | null;
  isLoading: boolean;
  refreshCache: (data: T) => void;
  /** Bound to the key of the render it came from; do not hold it across key changes. */
  refetch: () => Promise<void>;
};

// Global cache storage - using a Map for better performance
const globalCache = new Map<string, CacheEntry<any>>();

// Serialize query key to string for cache key
const serializeQueryKey = (key: QueryKey): string => {
  if (key === null || key === undefined) {
    return '';
  }
  if (typeof key === 'string') {
    return key;
  }
  if (Array.isArray(key)) {
    return JSON.stringify(key);
  }
  if (typeof key === 'object') {
    return JSON.stringify(key);
  }
  return String(key);
};

const buildCacheKey = (cacheKeyPrefix: string, key: QueryKey): string => {
  const serialized = serializeQueryKey(key);
  return cacheKeyPrefix ? `${cacheKeyPrefix}:${serialized}` : serialized;
};

/**
 * Removes every cached entry under a given `cacheKeyPrefix` namespace — e.g.
 * a screen clearing everything it cached once the user navigates away from
 * it, regardless of which individual keys (records, pages, etc.) were
 * fetched while it was mounted.
 */
export const clearQueryCacheNamespace = (cacheKeyPrefix: string): void => {
  const prefix = `${cacheKeyPrefix}:`;
  Array.from(globalCache.keys()).forEach(key => {
    if (key.startsWith(prefix)) {
      globalCache.delete(key);
    }
  });
};

const useQuery = <T>(
  queryKey: QueryKey,
  queryFn: QueryFunction<T>,
  options: UseQueryOptions = {}
): UseQueryReturn<T> => {
  const {
    cacheTTL = 3 * 60 * 1000, // Default 3 minutes
    cacheKeyPrefix = '',
    autoLoadingEnabled = true
  } = options;

  const getCacheKey = useCallback(
    (key: QueryKey) => buildCacheKey(cacheKeyPrefix, key),
    [cacheKeyPrefix]
  );

  const [data, setData] = useState<T>({} as T);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string | null>(null);

  const mounted = useRef(true);
  const currentRequestId = useRef<string | null>(null);
  const requestCounter = useRef(0);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const isRequestValid = useCallback((requestId: string) => {
    return currentRequestId.current === requestId && mounted.current;
  }, []);

  const handleRequestError = useCallback((error: Error) => {
    // A cancellation is never recorded as an error. With unique request ids a
    // superseded request no longer reaches this handler, so a cancellation that
    // does is the current request being cancelled with nothing to replace it
    // (auto-loading switched off mid-flight); end the loading state or it
    // would stay on forever. axios names its cancellation 'CanceledError',
    // which the AbortError checks below never matched.
    if (axios.isCancel(error)) {
      setIsLoading(false);
      return;
    }
    if (error instanceof Error && error.name !== 'AbortError') {
      setErrors(error.message);
    } else if (typeof error === 'string' && error !== 'AbortError') {
      setErrors(error);
    }
    setIsLoading(false);
  }, []);

  const handleRequestSuccess = useCallback(
    (response: T, now: number, key: QueryKey) => {
      const cacheKey = getCacheKey(key);
      globalCache.set(cacheKey, { data: response, timestamp: now });
      setData(response);
      setErrors(null);
      setIsLoading(false);
    },
    [getCacheKey]
  );

  const handleCachedData = useCallback((cached: CacheEntry<T>) => {
    setData(cached.data);
    // A cache hit is a successful load too; an error left by another key must
    // not outlive it.
    setErrors(null);
    setIsLoading(false);
  }, []);

  const refreshCache = useCallback(
    (newData: T) => {
      if (queryKey) {
        const cacheKey = getCacheKey(queryKey);
        globalCache.set(cacheKey, { data: newData, timestamp: Date.now() });
        setData(newData);
      }
    },
    [queryKey, getCacheKey]
  );

  const loadData = useCallback(
    async (bypassCache: boolean = false) => {
      if (queryKey === null || queryKey === undefined || queryKey === '') {
        // The effect teardown has already revoked and cancelled any request
        // that was in flight; this branch only resets what is shown.
        setData({} as T);
        setIsLoading(false);
        setErrors(null);
        return;
      }

      const serializedKey = serializeQueryKey(queryKey);
      // A counter rather than Date.now(): two loads started in the same
      // millisecond (a key change and a refetch in one effects flush) would
      // otherwise share an id, and the superseded one would pass the validity
      // check and write its outcome over the live request's state.
      requestCounter.current += 1;
      const requestId = `${serializedKey}-${requestCounter.current}`;
      currentRequestId.current = requestId;

      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Operation canceled due to new request.');
      }

      cancelTokenRef.current = axios.CancelToken.source();

      const now = Date.now();
      const cacheKey = getCacheKey(queryKey);
      const cached = globalCache.get(cacheKey);

      if (!bypassCache && cached && now - cached.timestamp < cacheTTL) {
        if (isRequestValid(requestId)) {
          handleCachedData(cached);
        }
        return;
      }

      if (mounted.current) {
        setIsLoading(true);
        setErrors(null);
      }

      try {
        const response = await queryFn(queryKey, cancelTokenRef.current.token);

        if (isRequestValid(requestId)) {
          handleRequestSuccess(response, now, queryKey);
        }
      } catch (error) {
        if (isRequestValid(requestId)) {
          handleRequestError(error as Error);
        }
      }
    },
    [
      queryKey,
      queryFn,
      cacheTTL,
      getCacheKey,
      isRequestValid,
      handleCachedData,
      handleRequestSuccess,
      handleRequestError
    ]
  );

  const refetch = useCallback(async () => {
    await loadData(true); // Bypass cache
  }, [loadData]);

  useEffect(() => {
    mounted.current = true;
    if (autoLoadingEnabled) {
      loadData();
    } else {
      // Nothing will replace a request the teardown below just revoked, so the
      // loading state has to end here.
      setIsLoading(false);
    }
    return () => {
      mounted.current = false;
      // This effect instance owned whatever request is in flight. Once it is
      // torn down that request may no longer write state, whether or not the
      // cancel reaches it in time - so revoke its id here rather than relying
      // on every path that supersedes a request to remember to.
      currentRequestId.current = null;
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted, request canceled.');
        cancelTokenRef.current = null;
      }
    };
  }, [loadData, autoLoadingEnabled]);

  return { data, errors, isLoading, refreshCache, refetch };
};

export default useQuery;
